import type { GatewayBrowserClient } from "../gateway.js";
import type { EmployeeRow, ConnectorInfo, SyncHistoryRow } from "../views/employees.js";

export type EmployeeAccountOptions = {
  authProfiles: Array<{
    profileId: string;
    provider?: string;
    mode?: string;
    label?: string;
    available?: boolean;
  }>;
  channelAccounts: Array<{
    channelId: string;
    accountId: string;
    label?: string;
    isDefault?: boolean;
  }>;
};

export type EmployeesState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  employeesLoading: boolean;
  employeesError: string | null;
  employeesList: EmployeeRow[] | null;
  employeesConnectors: ConnectorInfo[] | null;
  employeesSyncHistory: SyncHistoryRow[] | null;
  employeesStatus: unknown;
  employeesAccountOptions: EmployeeAccountOptions | null;
  selectedEmployee: EmployeeRow | null;
  showCreateForm: boolean;
};

type EmployeesListResponse = { employees?: unknown[] };

type EmployeesConnectorsResponse = { connectors?: ConnectorInfo[] };

type EmployeesSyncHistoryResponse = { history?: SyncHistoryRow[] };

type EmployeesAccountOptionsResponse = EmployeeAccountOptions;

function isEmployeeAccountOptions(val: unknown): val is EmployeeAccountOptions {
  if (!val || typeof val !== "object") {
    return false;
  }
  const v = val as Record<string, unknown>;
  if (!Array.isArray(v.authProfiles) || !Array.isArray(v.channelAccounts)) {
    return false;
  }
  return true;
}

export async function loadEmployees(state: EmployeesState) {
  if (!state.client || !state.connected) {
    return;
  }
  if (state.employeesLoading) {
    return;
  }
  state.employeesLoading = true;
  state.employeesError = null;
  try {
    const selectedId = state.selectedEmployee?.employee.employeeId ?? null;
    const res = await state.client.request<EmployeesListResponse>("employees.list", {});

    state.employeesList = normalizeEmployeesResponse(res);

    if (selectedId) {
      setSelectedEmployee(state, selectedId);
    }
  } catch (err) {
    state.employeesError = String(err);
  } finally {
    state.employeesLoading = false;
  }
}

export async function loadEmployeesConnectors(state: EmployeesState) {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    const res = await state.client.request<EmployeesConnectorsResponse>(
      "employees.sync.connectors.list",
      {},
    );
    state.employeesConnectors = Array.isArray(res.connectors) ? res.connectors : [];
  } catch (err) {
    console.error("Failed to load connectors:", err);
  }
}

export async function loadEmployeesStatus(state: EmployeesState) {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    const res = await state.client.request("employees.status", {});
    state.employeesStatus = res;
  } catch (err) {
    console.error("Failed to load status:", err);
  }
}

export async function loadEmployeesSyncHistory(state: EmployeesState) {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    const res = await state.client.request<EmployeesSyncHistoryResponse>("employees.sync.history", {
      limit: 10,
    });
    state.employeesSyncHistory = Array.isArray(res.history) ? res.history : [];
  } catch (err) {
    console.error("Failed to load sync history:", err);
  }
}

export async function loadEmployeesAccountOptions(state: EmployeesState) {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    const res = await state.client.request<EmployeesAccountOptionsResponse>(
      "employees.accounts.options",
      {},
    );
    state.employeesAccountOptions = isEmployeeAccountOptions(res) ? res : null;
  } catch (err) {
    console.error("Failed to load account options:", err);
  }
}

export async function loadEmployeesData(state: EmployeesState) {
  await Promise.all([
    loadEmployees(state),
    loadEmployeesConnectors(state),
    loadEmployeesStatus(state),
    loadEmployeesSyncHistory(state),
    loadEmployeesAccountOptions(state),
  ]);
}

export async function createEmployee(
  state: EmployeesState,
  employeeData: unknown,
  agentId?: string,
) {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    await state.client.request("employees.create", {
      employee: employeeData,
      agentId,
    });
    await loadEmployees(state);
    const employeeId =
      employeeData && typeof employeeData === "object" && "employeeId" in employeeData
        ? (employeeData as { employeeId?: string }).employeeId
        : undefined;
    if (employeeId) {
      setSelectedEmployee(state, employeeId);
    }
  } catch (err) {
    state.employeesError = String(err);
  }
}

export async function deleteEmployee(state: EmployeesState, employeeId: string) {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    await state.client.request("employees.delete", {
      employeeId,
    });
    await loadEmployees(state); // Refresh list
    if (state.selectedEmployee?.employee.employeeId === employeeId) {
      state.selectedEmployee = null;
    }
  } catch (err) {
    state.employeesError = String(err);
  }
}

export function setSelectedEmployee(state: EmployeesState, employeeId: string | null) {
  state.selectedEmployee = employeeId
    ? state.employeesList?.find((e: EmployeeRow) => e.employee.employeeId === employeeId) || null
    : null;
}

export async function updateEmployee(
  state: EmployeesState,
  employeeId: string,
  data: Partial<unknown>,
) {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    await state.client.request("employees.update", {
      employeeId,
      data,
    });
    await loadEmployees(state);
    setSelectedEmployee(state, employeeId);
  } catch (err) {
    state.employeesError = String(err);
  }
}

export async function addEmployeeChannel(
  state: EmployeesState,
  employeeId: string,
  channelId: string,
) {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    await state.client.request("employees.channels.add", {
      employeeId,
      channelId,
    });
    await loadEmployees(state);
    setSelectedEmployee(state, employeeId);
  } catch (err) {
    state.employeesError = String(err);
  }
}

export async function removeEmployeeChannel(
  state: EmployeesState,
  employeeId: string,
  channelId: string,
) {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    await state.client.request("employees.channels.remove", {
      employeeId,
      channelId,
    });
    await loadEmployees(state);
    setSelectedEmployee(state, employeeId);
  } catch (err) {
    state.employeesError = String(err);
  }
}

export async function runEmployeesSync(state: EmployeesState, dryRun: boolean) {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    const selectedId = state.selectedEmployee?.employee.employeeId ?? null;
    await state.client.request("employees.sync.run", {
      dryRun,
    });
    await Promise.all([loadEmployees(state), loadEmployeesSyncHistory(state)]);
    if (selectedId) {
      setSelectedEmployee(state, selectedId);
    }
  } catch (err) {
    state.employeesError = String(err);
  }
}

export async function testGeneralConnection(state: EmployeesState) {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    await state.client.request("employees.sync.run", { dryRun: true });
    await loadEmployeesSyncHistory(state);
  } catch (err) {
    state.employeesError = String(err);
  }
}

/**
 * Assign or update the role for an employee. Thin wrapper around the
 * generic update RPC; exposed separately so the UI can wire it directly
 * and tests can target a stable surface.
 */
export async function assignEmployeeRole(state: EmployeesState, employeeId: string, role: string) {
  const trimmed = role.trim();
  if (!trimmed) {
    state.employeesError = "Role cannot be empty.";
    return;
  }
  await updateEmployee(state, employeeId, { role: trimmed });
}

/**
 * Normalise a raw API response into the `EmployeeRow[]` shape used by
 * the view. Tolerates both `employees` and `items` keys and rows that
 * are missing `agentId` (synthesised from `employeeId`). Exported for
 * unit tests.
 */
export function normalizeEmployeesResponse(res: unknown): EmployeeRow[] {
  const r = (res ?? {}) as Record<string, unknown>;
  const rawList = Array.isArray(r.employees) ? r.employees : Array.isArray(r.items) ? r.items : [];
  return rawList
    .map((raw: unknown) => {
      if (!raw || typeof raw !== "object") return null;
      const rawObj = raw as Record<string, unknown>;
      const agentIdRaw =
        typeof rawObj.agentId === "string" && rawObj.agentId.trim()
          ? rawObj.agentId
          : typeof rawObj.employeeId === "string" && rawObj.employeeId.trim()
            ? `employee-${rawObj.employeeId}`
            : null;
      if (!agentIdRaw) return null;
      const { agentId: _a, ...employee } = rawObj;
      return { agentId: agentIdRaw, employee } as EmployeeRow;
    })
    .filter((row): row is EmployeeRow => Boolean(row));
}
